import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
  canonical?: string;
}

const DEFAULT_TITLE = 'Cognidaily - Daily Competitive Trivia';
const DEFAULT_DESCRIPTION = 'The platform for seriously competitive learners. Test your knowledge daily with 10 curated questions. Compete globally, track your progress, and climb the leaderboard.';
const DEFAULT_IMAGE = '/og-image.png';
const SITE_URL = 'https://cognidaily.io';

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  canonical
}) => {
  const location = useLocation();
  
  // Build full URL
  const fullUrl = url || `${SITE_URL}${location.pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const canonicalUrl = canonical || fullUrl;
  const pageTitle = title ? `${title} | Cognidaily` : DEFAULT_TITLE;

  useEffect(() => {
    // Helper function to set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Update document title
    document.title = pageTitle;

    // Basic meta tags
    setMetaTag('description', description, false);
    if (keywords) {
      setMetaTag('keywords', keywords, false);
    }
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow', false);
    setMetaTag('author', 'Cognidaily', false);
    setMetaTag('viewport', 'width=device-width, initial-scale=1', false);

    // Open Graph tags
    setMetaTag('og:title', pageTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', fullImageUrl, true);
    setMetaTag('og:url', fullUrl, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:site_name', 'Cognidaily', true);
    setMetaTag('og:locale', 'en_US', true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image', false);
    setMetaTag('twitter:title', pageTitle, false);
    setMetaTag('twitter:description', description, false);
    setMetaTag('twitter:image', fullImageUrl, false);
    setMetaTag('twitter:url', fullUrl, false);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Cleanup function
    return () => {
      // Optionally reset to defaults if needed
    };
  }, [pageTitle, description, keywords, fullImageUrl, fullUrl, type, noindex, canonicalUrl]);

  return null; // This component doesn't render anything
};

export default SEOHead;

